import { assign, createMachine, interpret, Interpreter, spawn } from 'xstate'
import isNotAnonAxiosError from '~~/utility/isNotAnonAxiosError'
import sentryCapture from '~~/utility/sentryCapture'
import { assertEventType } from '~~/utility/xstate/assertEvent'
import { createFavouriteMachine } from './favourite.machine'
import { FavouriteRepositoryAjax } from './FavouriteRepositoryFetch'
import { FavouritesFetch } from './FavouritesFetch'
import { FavouriteCampsiteDetails } from './types'

const RETRIES = 3

type Events =
  | { type: 'GET_BUTTON'; id: string }
  | { type: 'REMOVE_BUTTON'; id: string }
  | { type: 'CLEAR_ALL' }
  | { type: 'FETCH_ALL'; data: string[] }
  | { type: 'FAVOURITE_ADD'; id: string }
  | { type: 'FAVOURITE_REMOVE'; id: string }
  | { type: 'CLEAR_ALL_DETAILS' }
  | {
    type: 'FETCH_ALL_DETAILS'
    data: { page: number; numPages: number; results: any[] } // TODO: feed it with new type
  }

export interface FavouritesCampsiteMachineContext {
  favouritesDetails: Record<string, FavouriteCampsiteDetails>
  favourites: Array<{ id: string; added: Date }>
  fetching: {
    page: number
    retries: number
  }
  currentLanguage: string
  activeFavourites: {
    id: string
    ref: any
  }[]
}

export function getInitialContext(): FavouritesCampsiteMachineContext {
  return {
    favourites: [],
    favouritesDetails: {},
    fetching: {
      page: 1,
      retries: 0,
    },
    currentLanguage: 'en-gb',
    activeFavourites: [],
  }
}

export const createFavouritesMachine = ({
  currentLanguage,
}: {
  currentLanguage: string
}) =>
  createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QDMCGA3A9gVwE4EsAXOAOgAtUIAbfAOygCFtDDNbYBiAcQFEAVAPoMAqnz4B5AHIBtAAwBdRKAAOmWEXxslIAB6IAzAFZ9JABwBGfQE4rs-RYAspqwCYANCACeiALTnD5iSytqYAbC4A7BF2NrIRAL7xHmhYeESkFNR0jMys7BwAYgCCAGriwgBKAJJ8PAJFACINcopIIKrqhJq02noI4aYkVuYRhhH2AcGh+h7eCH6WJEamcRHhVoYOruaJyRg4BMSw5JQ09EwsbJzFZZU1dRU8ALLiJTwt2h0aWm1904FrKzjFYjazmUyzXz6ewkUIRBwjBHRFwI0y7EApA7pY7IMCEADGZGyBX2aSOJFxBKJ9A4EDYYBIdCwAGsGZiyaRKYTiaTDpy8dz6AgmZh8agumwWh82l8JT1fohzC4rCYUTYtqZJv5IfMXKErCRIrJNnZ9HDzLI4ej2XycQLqVASalbRT7dkOGBcLhMLgSMoqOLkD6ALYU3nY11UnnOiNch3C2hYMVyqUKT5qb7y0B9C2WkjmJUOCIuFxjLbmBw6vwlpayBwOFwW0v6JUJJIY8PkuPRrFHBp41D4KjXfgAYQAEvUADJTgQNfhFKpTgDK0pUGblvUVowisP8FZc9miY1COoLoRIDmsjeVZtsEQ21s7-Kj9Cdvbg-cIg+HkcFUD9b18TgdQaTpWgGRFVkwxjLs3TfZ9YC-H87VfADlCAkDsgTJNxW6VNWnXTpui3BBLFGIIjGhCtQgcUJTDCHUjF3WjDGMQxTGVDjNQcJ9YJff93w5JCByHVD-0A0UsLA+lGUTTBoJtWN4MdRDkLEv8HUk4DYFAqAcNFPDJQUaRzEI9oNxIhUyNRIZQlkTV-BcBjDHCHVDEbMx9RsWwVVc4s+I-cSHSE211N-bt6G06SoFpWSoLZRDNJ7YTwuC7Jot07CRWTfCTJcczZSs7NFSsJwgmiS14TrKxQkMdz9FkWFry40JLFkcxQkC4TkoQ-iRO-DTIvQzCsppT1vV9f1AxDGCgt61T+rShbMr0gzcuM+Q1ws4ifhKsiInMA0Hw4wxatkOsVhmLxEFiIJjGVUwET1ByXG6l1htC7FluGkhcDxXBPHdbair23Rt1sWFDyMWQUTNZVKxusiCya8YHFhgIPLNS13uUtCvr7USIpUv6AaBmlTMKyywZzcETGGOE4WvS1pjPJUXCWax4U2Q7G3rRJ21oTAIDgbQlKOdNdqzcHdWVWE4UicFS02Fw7CresHCh40zQRLHRlx8lMjOHJLnYSXM1IvwLHl4tDs4tiGzVpGfEPQY1g4qwGLa4ZTH0Ns9n6k4snOXIrkZagwHNzdrL8aEbcV+2VaduYjovVsyvsMrwXR-QDYyU5sguPJjkoCBC9D6XQelvpY5MUIFbt5XHeuuZoSa+yEaBaHxhx9txYEkLEKj4qZdsQxDSvDiG3rNvzCrIwLzKti1gbTjlSBPP0r6+b8Aj4eacVcJx4rDznIiFZIlPZ2UUCGei3sgJDotLq+6Sz635U-fq9u4wJ+WaeryNTntfOqlFghRCenEWGudX6B3fktIm8AZTU2-ggEIl5XInlOmPKwOpnIkBXiMQ6oxXC1X0G9WB814FBWWrAMgmAADu2Qv6kQ2BeBwmD3aahwTqVeBCixFgLBsSeaxN4LQJp+RBJA6GMIymAYMyhCBzCIhbayuZNYcLqlws6xpcFIwCOwkshg4jOX1GxMR1DUpSJkUwqK-1YDYCoIQJBKjo77XURgrRdVuG6J1HVJqzhRjBDGGMNGFiVISIGihaR9DbEAQmj6FhaiLoaM4d4nRZ0dTn3Hn7UsBYwgcOGP7DscCIlqSkcNJJ7jyG7jsMYFsdE6JezwfqIYx5RhPVLBYcJ+NymDWJmhcOVBI7IKlqREs9YSBsRWOfSwV5bBXzmNEEwphRh+2sCqX2OxKE9UsWFCpJMMJSTGlAKpMtVacUvOdYYepsl6LmCsC8h56IMVWc4RqvEdkfTKQg-pW8AL-UIIDZhozVHuLGMdB89lYZXnWIjFOSsCH6HRnkrG9kX6JCAA */
      id: 'favourites',
      initial: 'fetchingFavourites',
      context: {
        ...getInitialContext(),
        currentLanguage: currentLanguage,
      },
      schema: {
        events: {} as Events,
        context: {} as FavouritesCampsiteMachineContext,
      },
      type: 'parallel',
      states: {
        handlingButtons: {
          on: {
            GET_BUTTON: 'handlingButtons.addingButton',
            REMOVE_BUTTON: 'handlingButtons.removingButton',
            FAVOURITE_ADD: { actions: 'addFavourite' },
            FAVOURITE_REMOVE: { actions: 'removeFavourite' },
          },
          initial: 'idle',
          states: {
            idle: {},
            addingButton: {
              entry: 'assignNewFavouriteButtonMachine',
              always: 'idle',
            },
            removingButton: {
              entry: 'clearButtonMachine',
              always: 'idle',
            },
          },
        },
        fetchingFavourites: {
          initial: 'fetchingAll',
          id: 'fetchingFavourites',
          on: {
            FETCH_ALL: {
              target: '#fetchingFavourites.fetchingAll',
            },
            CLEAR_ALL: {
              target: ['#showing.empty', '#fetchingDetails.idle'],
              actions: 'clearAllFavourites',
            },
          },
          states: {
            idle: {},
            fetchingAll: {
              invoke: {
                src: 'fetchFavouritesService',
                onDone: {
                  target: 'idle',
                  actions: [
                    'assignFavourites',
                    (context) => {
                      // update children buttons if in favourites list
                      context.favourites.forEach((f) => {
                        const button = context.activeFavourites[f.id]
                        if (button) button.ref.send('SET_FAVOURITE')
                      })
                    },
                  ],
                },
                onError: {
                  target: 'idle',
                  actions: 'logError',
                },
              },
            },
          },
        },
        fetchingFavouritesDetails: {
          type: 'parallel',
          on: {
            FETCH_ALL_DETAILS: '#fetchingDetails.processing',
            CLEAR_ALL_DETAILS: {
              target: '#showing.empty',
              actions: 'clearAllFavouritesDetails',
            },
          },
          states: {
            showing: {
              initial: 'empty',
              id: 'showing',
              states: {
                empty: {},
                results: {},
                error: {},
              },
            },
            fetching: {
              initial: 'idle',
              id: 'fetchingDetails',
              states: {
                idle: {},
                processing: {
                  invoke: {
                    src: 'fetchDetailsBatch',
                    onDone: [
                      {
                        actions: ['setFetchingData', 'addFavouritesDetails'],
                        target: ['processing', '#showing.results'],
                        cond: 'hasNextPageToFetch',
                      },
                      {
                        actions: 'addFavouritesDetails',
                        target: ['idle', '#showing.results'],
                        cond: 'favouritesDetailsExists',
                      },
                      {
                        target: ['idle', '#showing.empty'],
                      },
                    ],
                    onError: {
                      target: 'retrying',
                      actions: 'logError',
                    },
                  },
                  exit: 'resetFetchingInfo',
                },
                retrying: {
                  always: [
                    {
                      target: 'processing',
                      actions: 'riseFetchingIteration',
                      cond: ({ fetching }) => fetching.retries < RETRIES,
                    },
                    {
                      target: '#showing.error',
                    },
                  ],
                },
              },
            },
          },
        },
      },
    },
    {
      guards: {
        hasNextPageToFetch: (_, { data }: any) => {
          return data.page < data.numPages
        },
        favouritesDetailsExists: (_, { data }: any) => {
          return data.results.length
        },
      },
      actions: {
        resetFetchingInfo: assign({
          fetching: (_) => getInitialContext()['fetching'],
        }),
        riseFetchingIteration: assign({
          fetching: ({ fetching }) => ({
            ...fetching,
            retries: ++fetching.retries,
          }),
        }),
        clearButtonMachine: assign({
          activeFavourites: ({ activeFavourites }, event: any) => {
            const buttons = { ...activeFavourites }
            delete buttons[event.id]
            return buttons
          },
        }),
        addFavourite: assign({
          favourites: (context, event) => {
            assertEventType(event, 'FAVOURITE_ADD')
            const newFavourites = context.favourites
            newFavourites.push({
              id: event.id,
              added: new Date(),
            })
            return newFavourites
          },
        }),
        removeFavourite: assign({
          favourites: (context, event) => {
            assertEventType(event, 'FAVOURITE_REMOVE')
            return (
              context.favourites.filter((fav) => fav.id !== event.id) ||
              context.favourites
            )
          },
          favouritesDetails: (context, event) => {
            assertEventType(event, 'FAVOURITE_REMOVE')
            const favDetails = context.favouritesDetails
            delete favDetails[event.id]
            return favDetails
          },
        }),
        assignFavourites: assign({
          favourites: (_, event: any) => {
            return event.data.map(
              (fav: { campsiteId: string; added: Date }) => ({
                id: fav.campsiteId,
                added: fav.added ? fav.added : '',
              }),
            )
          },
        }),
        setFetchingData: assign({
          fetching: (context, evt: any) => {
            return {
              ...context.fetching,
              page: evt.data.page + 1,
            }
          },
        }),
        assignNewFavouriteButtonMachine: assign({
          activeFavourites: (context, event) => {
            const favs = context.activeFavourites || {}
            assertEventType(event, 'GET_BUTTON')
            const favourite = context.favourites.find((f) => f.id === event.id)
            favs[event.id] = {
              ...event,
              ref: spawn(
                createFavouriteMachine({
                  id: event.id,
                  isFavourite: !!favourite,
                  added: favourite?.added,
                }),
              ),
            }
            return favs
          },
        }),
        clearAllFavourites: assign({
          favourites: (_) => [],
          favouritesDetails: (_) => ({}),
        }),
        clearAllFavouritesDetails: assign({
          favouritesDetails: (_) => ({}),
        }),
        addFavouritesDetails: assign<FavouritesCampsiteMachineContext, any>({
          favouritesDetails: ({ favourites, favouritesDetails }, { data }) => {
            return data.results.reduce((result, item) => {
              const favourite = favourites.find((f) => f.id === item.id)
              result[item.id] = {
                ...item,
                added: favourite ? favourite.added : '',
              }
              return result
            }, favouritesDetails)
          },
        }),
        logError: (context, event) => logError(context, event),
      },
      services: {
        fetchDetailsBatch: async ({ fetching, currentLanguage }) => {
          const results = await new FavouritesFetch().fetch(
            currentLanguage,
            fetching.page,
          )
          return results
        },
        fetchFavouritesService: async ({ currentLanguage }) => {
          return await new FavouriteRepositoryAjax().readAll(currentLanguage)
        },
      },
    },
  )

export type FavouritesMachineInterpreter = Interpreter<
FavouritesCampsiteMachineContext,
any,
Events
>

export function getFavouritesNewMachine(languageCode: string) {
  return interpret(
    createFavouritesMachine({ currentLanguage: languageCode }),
  ).start()
}
let favMachine: ReturnType<typeof getFavouritesNewMachine>

// get the favourites machine as a singleton
export function getFavouritesMachine(languageCode: string) {
  if (process.server) {
    throw Error('Trying to getFavouritesMachine singleton on server')
  }

  if (favMachine) {
    return favMachine
  }
  favMachine = getFavouritesNewMachine(languageCode)
  return favMachine
}

const logError = (_, event: any) => {
  if (!event || !event.data || !event.data.response) return
  const response = event.data.response
  if (response && (response.status === 403 || response.statusCode === 403))
    return
  // aborted connection, happens only for bytespider bot
  if (event.code === 'ECONNABORTED') return
  if (isNotAnonAxiosError(event.data)) {
    event.data.name = `favouritesMachine ${event.type} error - ${
      event.code ? event.code : ''
    }`
    sentryCapture(event.data)
  }
}
